import React, { useState } from 'react';
import { aboutContent } from '../../content/about-content';
import { BrainDiagram } from './infographics';
import NeuroInformedBadge from '../NeuroInformedBadge';

/**
 * Brain Anatomy Section
 *
 * Interactive brain diagram with region descriptions.
 */
const BrainAnatomySection = React.forwardRef(({ id = 'brain' }, ref) => {
  const { brainAnatomy } = aboutContent;
  const [activeRegion, setActiveRegion] = useState(null);

  const handleRegionClick = (regionId) => {
    setActiveRegion(activeRegion === regionId ? null : regionId);
  };

  return (
    <section id={id} ref={ref} className="about-section">
      <header className="about-section__header">
        <h2 className="about-section__title">
          <svg className="about-section__title-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 2a4 4 0 0 1 4 4c0 1.1-.4 2.1-1 2.9.6.8 1 1.8 1 2.9 0 1.1-.4 2.1-1 2.9.6.8 1 1.8 1 2.9a4 4 0 0 1-4 4" />
            <path d="M12 2a4 4 0 0 0-4 4c0 1.1.4 2.1 1 2.9-.6.8-1 1.8-1 2.9 0 1.1.4 2.1 1 2.9-.6.8-1 1.8-1 2.9a4 4 0 0 0 4 4" />
          </svg>
          {brainAnatomy.title}
        </h2>
        <p className="about-section__subtitle">{brainAnatomy.intro}</p>
      </header>

      <div className="brain-anatomy-container">
        {/* Interactive Brain Diagram */}
        <div className="brain-diagram-wrapper">
          <BrainDiagram
            activeRegion={activeRegion}
            onRegionClick={handleRegionClick}
            size="large"
            interactive={true}
            showLabels={true}
          />
        </div>

        {/* Region Info Cards */}
        <div className="brain-regions-list">
          {brainAnatomy.regions.map((region) => (
            <div
              key={region.id}
              className={`brain-region-card ${activeRegion === region.id ? 'brain-region-card--active' : ''}`}
              style={{
                borderLeftColor: activeRegion === region.id ? region.color : undefined
              }}
              onClick={() => handleRegionClick(region.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleRegionClick(region.id);
                }
              }}
              aria-expanded={activeRegion === region.id}
            >
              <div className="brain-region-card__header">
                <span
                  className="brain-region-card__dot"
                  style={{ backgroundColor: region.color }}
                  aria-hidden="true"
                />
                <span className="brain-region-card__name">{region.name}</span>
                <span className="brain-region-card__nickname">{region.nickname}</span>
              </div>

              {activeRegion === region.id && (
                <div className="brain-region-card__details">
                  <p style={{ margin: 0 }}><strong>Function:</strong> {region.function}</p>
                  <div className="brain-region-card__lrb">
                    <strong>LRB Connection:</strong> {region.lrbConnection}
                    <div style={{ marginTop: '0.5rem' }}>
                      <NeuroInformedBadge
                        technique={region.technique}
                        variant="subtle"
                        size="small"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
});

BrainAnatomySection.displayName = 'BrainAnatomySection';

export default BrainAnatomySection;
